import type { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';

export const HEAVY_UNIT_THRESHOLD = 20;
export const VERY_HEAVY_UNIT_THRESHOLD = 50;
export const LARGE_DATE_RANGE_DAYS = 395;
export const VERY_LARGE_DATE_RANGE_DAYS = 730;

export type WarningSeverity = 'info' | 'warning' | 'heavy';

export interface FilterWarning {
  message: string;
  severity: WarningSeverity;
}

export interface FilterValidationResult {
  isValid: boolean;
  warnings: FilterWarning[];
}

function calculateDateRangeDays(startDate?: Date, endDate?: Date): number | null {
  if (!startDate || !endDate) return null;
  return Math.abs((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
}

export function validateAnalyticsFilters(filters?: BaseAnalyticsFilter): FilterValidationResult {
  const warnings: FilterWarning[] = [];

  if (!filters) {
    return { isValid: true, warnings };
  }

  if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
    warnings.push({
      message: 'End date is before start date',
      severity: 'warning',
    });
    return { isValid: true, warnings };
  }

  const unitCount = filters.unitIds?.length || 0;
  const daysDiff = calculateDateRangeDays(filters.startDate, filters.endDate);
  const isAllTime = !filters.startDate || !filters.endDate;

  if (isAllTime && unitCount >= VERY_HEAVY_UNIT_THRESHOLD) {
    warnings.push({
      message: `Loading all-time data for ${unitCount} bases may take several minutes`,
      severity: 'heavy',
    });
  } else if (isAllTime && unitCount >= HEAVY_UNIT_THRESHOLD) {
    warnings.push({
      message: `Loading all-time data for ${unitCount} bases may take 1-2 minutes`,
      severity: 'warning',
    });
  } else if (unitCount >= VERY_HEAVY_UNIT_THRESHOLD) {
    warnings.push({
      message: `Loading data for ${unitCount} bases may take 1-2 minutes`,
      severity: 'warning',
    });
  } else if (unitCount >= HEAVY_UNIT_THRESHOLD) {
    warnings.push({
      message: `Loading data for ${unitCount} bases may take 30-60 seconds`,
      severity: 'info',
    });
  }

  if (daysDiff && daysDiff > VERY_LARGE_DATE_RANGE_DAYS) {
    warnings.push({
      message: `Large date range (${Math.round(daysDiff / 365)} years) may take longer to load`,
      severity: 'info',
    });
  } else if (daysDiff && daysDiff > LARGE_DATE_RANGE_DAYS) {
    warnings.push({
      message: 'Date range over 13 months may take longer to load',
      severity: 'info',
    });
  }

  return {
    isValid: true,
    warnings,
  };
}

export function getFilterValidationMessage(result: FilterValidationResult): string | null {
  if (result.warnings.length > 0) {
    return result.warnings.map(w => w.message).join('. ');
  }
  return null;
}
